import moment from 'moment'
import { v1 as uuid } from 'uuid'

import {
  ContentFlow,
  ContentFlowDatePicker,
  ContentFlowElement,
  ContentFlowMultiSelect,
  ContentFlowPage,
  ContentFlowSingleSelect,
  ContentFlowText,
} from '@/app/module/campaigns/types/common'
import { createCompactId } from '@/app/module/campaigns/utils/actions/ids'

export const BUTTON_MAX_LENGTH = 22
export const MESSAGE_MAX_LENGTH = 1024
export const PAGE_TITLE_MAX_LENGTH = 60

export const HEADING_TEXT_MAX_LENGTH = 80
export const SUBHEADING_TEXT_MAX_LENGTH = 80

export const SHORT_TEXT_QUESTION_MAX_LENGTH = 60
export const SHORT_TEXT_HELP_TEXT_MAX_LENGTH = 60

export const LONG_TEXT_QUESTION_MAX_LENGTH = 60
export const LONG_TEXT_HELP_TEXT_MAX_LENGTH = 60
export const FOOTER_LABEL_MAX_LENGTH = 35
export const DATE_PICKER_QUESTION_MAX_LENGTH = 40

export const SELECT_QUESTION_MAX_LENGTH = 60
export const SELECT_ANSWER_MAX_LENGTH = 60

export const CHOICE_MAX = 20
export const PAGE_MAX = 10

const MIN_TEXT_LENGTH = 1
const DATE_PICKER_FORMAT = 'YYYY-MM-DD'

export const getWaFlowLengthError = (value: string, maxLength: number): string => {
  if (value.length < MIN_TEXT_LENGTH) {
    return 'This field is required.'
  }
  return value.length > maxLength ? `Meta limits this field to ${maxLength} characters.` : ''
}

const hasLengthValidationError = (value: string, maxLength: number): boolean =>
  Boolean(getWaFlowLengthError(value, maxLength))

export const isValidDatePickerValue = (value?: string): boolean => {
  if (!value) {
    return false
  }

  return moment(value, DATE_PICKER_FORMAT, true).isValid()
}

const hasElementValidationError = (element: ContentFlowElement): boolean => {
  if (element.heading && hasLengthValidationError(element.heading.text || '', HEADING_TEXT_MAX_LENGTH)) {
    return true
  }

  if (element.subheading && hasLengthValidationError(element.subheading.text || '', SUBHEADING_TEXT_MAX_LENGTH)) {
    return true
  }

  if (
    element.shortText &&
    (hasLengthValidationError(element.shortText.question || '', SHORT_TEXT_QUESTION_MAX_LENGTH) ||
      hasLengthValidationError(element.shortText.helpText || '', SHORT_TEXT_HELP_TEXT_MAX_LENGTH))
  ) {
    return true
  }

  if (
    element.longText &&
    (hasLengthValidationError(element.longText.question || '', LONG_TEXT_QUESTION_MAX_LENGTH) ||
      hasLengthValidationError(element.longText.helpText || '', LONG_TEXT_HELP_TEXT_MAX_LENGTH))
  ) {
    return true
  }

  if (element.singleSelect) {
    if (
      hasLengthValidationError(element.singleSelect.question || '', SELECT_QUESTION_MAX_LENGTH) ||
      element.singleSelect.answers.length < 1 ||
      element.singleSelect.answers.length > CHOICE_MAX
    ) {
      return true
    }

    if (
      element.singleSelect.answers.some((answer) =>
        hasLengthValidationError(answer.value || '', SELECT_ANSWER_MAX_LENGTH),
      )
    ) {
      return true
    }
  }

  if (element.multiSelect) {
    if (
      hasLengthValidationError(element.multiSelect.question || '', SELECT_QUESTION_MAX_LENGTH) ||
      element.multiSelect.answers.length < 1 ||
      element.multiSelect.answers.length > CHOICE_MAX
    ) {
      return true
    }

    if (
      element.multiSelect.answers.some((answer) =>
        hasLengthValidationError(answer.value || '', SELECT_ANSWER_MAX_LENGTH),
      )
    ) {
      return true
    }
  }

  if (element.datePicker) {
    const datePicker: ContentFlowDatePicker = element.datePicker
    if (
      hasLengthValidationError(datePicker.question || '', DATE_PICKER_QUESTION_MAX_LENGTH) ||
      Object.values(getWaFlowDateErrors(datePicker)).some(Boolean)
    ) {
      return true
    }
  }

  if (element.footer && hasLengthValidationError(element.footer.label || '', FOOTER_LABEL_MAX_LENGTH)) {
    return true
  }

  return false
}

export const hasWaFormEditorValidationErrors = (form?: Pick<ContentFlow, 'button' | 'pages' | 'text'>): boolean => {
  if (!form) {
    return true
  }

  if (hasLengthValidationError(form.text || '', MESSAGE_MAX_LENGTH)) {
    return true
  }

  if (hasLengthValidationError(form.button || '', BUTTON_MAX_LENGTH)) {
    return true
  }

  if (!form.pages?.length || form.pages.length > PAGE_MAX) {
    return true
  }

  return form.pages.some(hasWaFlowPageValidationErrors)
}

export const hasWaFlowPageValidationErrors = (page: ContentFlowPage): boolean =>
  hasLengthValidationError(page.title || '', PAGE_TITLE_MAX_LENGTH) ||
  !page.elements.some((element) => !element.footer) ||
  page.elements.some(hasElementValidationError)

export const getWaFlowDateErrors = (value: ContentFlowDatePicker) => {
  let minDateError = ''
  let maxDateError = ''
  if (!value.minDate) {
    minDateError = 'Earliest date is required.'
  } else if (!isValidDatePickerValue(value.minDate)) {
    minDateError = 'Use a valid YYYY-MM-DD date.'
  }
  if (!value.maxDate) {
    maxDateError = 'Latest date is required.'
  } else if (!isValidDatePickerValue(value.maxDate)) {
    maxDateError = 'Use a valid YYYY-MM-DD date.'
  }
  if (
    !minDateError &&
    !maxDateError &&
    moment(value.minDate, DATE_PICKER_FORMAT, true).isAfter(moment(value.maxDate, DATE_PICKER_FORMAT, true), 'day')
  ) {
    maxDateError = 'Latest date must be on or after earliest date.'
  }
  return {
    minDate: minDateError,
    maxDate: maxDateError,
    unavailableDates: value.unavailableDates?.some((date) => !isValidDatePickerValue(date))
      ? 'Use valid YYYY-MM-DD dates separated by commas.'
      : '',
  }
}

export const createHeadingElement = (id: string): ContentFlowElement => {
  return {
    heading: {
      text: '',
      translations: {},
    },
    questionId: id,
  }
}

export const createSubheadingElement = (id: string): ContentFlowElement => {
  return {
    questionId: id,
    subheading: {
      text: '',
      translations: {},
    },
  }
}

export const createShortTextElement = (id: string): ContentFlowElement => {
  return {
    questionId: id,
    shortText: {
      helpText: '',
      question: '',
      required: false,
    },
  }
}

export const createLongTextElement = (id: string): ContentFlowElement => {
  return {
    longText: {
      helpText: '',
      question: '',
      required: false,
    },
    questionId: id,
  }
}

export const createFooterElement = (id: string): ContentFlowElement => {
  return {
    footer: {
      label: '',
    },
    questionId: id,
  }
}

export const createSingleSelectElement = (id: string): ContentFlowElement => {
  return {
    questionId: id,
    singleSelect: {
      answers: [
        {
          id: createCompactId(),
          label: '',
          translations: {},
          value: '',
        },
      ],
      question: '',
      required: false,
      translations: {},
    },
  }
}

export const createMultiSelectElement = (id: string): ContentFlowElement => {
  return {
    multiSelect: {
      answers: [
        {
          id: createCompactId(),
          label: '',
          translations: {},
          value: '',
        },
      ],
      question: '',
      required: false,
      translations: {},
    },
    questionId: id,
  }
}

export const createDatePickerElement = (id: string): ContentFlowElement => {
  return {
    datePicker: {
      question: '',
    },
    questionId: id,
  }
}

export const appendElementToPage = (page: ContentFlowPage, elementType: string): ContentFlowPage => {
  const hasFooter = page.elements.some((element) => Boolean(element.footer))
  const nextId = uuid()
  let nextElement: ContentFlowElement | null = null

  switch (elementType) {
    case 'heading':
      nextElement = createHeadingElement(nextId)
      break
    case 'subheading':
      nextElement = createSubheadingElement(nextId)
      break
    case 'shortText':
      nextElement = createShortTextElement(nextId)
      break
    case 'longText':
      nextElement = createLongTextElement(nextId)
      break
    case 'singleSelect':
      nextElement = createSingleSelectElement(nextId)
      break
    case 'multiSelect':
      nextElement = createMultiSelectElement(nextId)
      break
    case 'datePicker':
      nextElement = createDatePickerElement(nextId)
      break
    case 'footer':
      if (hasFooter) {
        return page
      }

      nextElement = createFooterElement(nextId)
      break
    default:
      return page
  }

  if (!nextElement) {
    return page
  }

  return {
    ...page,
    elements: [...page.elements, nextElement],
  }
}

export const mergePageWithFooter = (page: ContentFlowPage, footerElement?: ContentFlowElement): ContentFlowPage => {
  if (!footerElement) {
    return page
  }

  return {
    ...page,
    elements: [...page.elements, footerElement],
  }
}

export const getElementTypeLabel = (element: ContentFlowElement): string => {
  switch (true) {
    case Boolean(element.heading):
      return 'Heading'
    case Boolean(element.subheading):
      return 'Subheading'
    case Boolean(element.singleSelect):
      return 'Single select'
    case Boolean(element.multiSelect):
      return 'Multi select'
    case Boolean(element.shortText):
      return 'Open Response (short)'
    case Boolean(element.longText):
      return 'Open Response (long)'
    case Boolean(element.datePicker):
      return 'Date picker'
    case Boolean(element.footer):
      return 'Footer'
    default:
      return 'Element'
  }
}

export const reorderPageElements = (
  page: ContentFlowPage,
  sourceIndex: number,
  destinationIndex: number,
): ContentFlowPage => {
  const elements = [...page.elements]

  if (
    sourceIndex < 0 ||
    destinationIndex < 0 ||
    sourceIndex >= elements.length ||
    destinationIndex >= elements.length
  ) {
    return page
  }

  const [removed] = elements.splice(sourceIndex, 1)

  if (!removed) {
    return page
  }

  elements.splice(destinationIndex, 0, removed)

  return {
    ...page,
    elements,
  }
}

export const updateElementHeading = (
  page: ContentFlowPage,
  elementId: string,
  key: 'heading' | 'subheading',
  value: string,
): ContentFlowPage => {
  const elements = [...page.elements]
  const elementIndex = elements.findIndex((element) => element.questionId === elementId)

  if (elementIndex === -1) {
    return page
  }

  const targetElement = elements[elementIndex]

  const displayText = targetElement[key]

  if (!displayText) {
    return page
  }

  elements[elementIndex] = {
    ...targetElement,
    [key]: {
      ...displayText,
      text: value,
    },
  }

  return {
    ...page,
    elements,
  }
}

export const updateElementTextField = (
  page: ContentFlowPage,
  elementId: string,
  key: 'shortText' | 'longText',
  value: ContentFlowText,
): ContentFlowPage => {
  const elements = [...page.elements]
  const elementIndex = elements.findIndex((element) => element.questionId === elementId)

  if (elementIndex === -1) {
    return page
  }

  const targetElement = elements[elementIndex]

  const textField = targetElement[key]

  if (!textField) {
    return page
  }

  elements[elementIndex] = {
    ...targetElement,
    [key]: value,
  }

  return {
    ...page,
    elements,
  }
}

export const updateElementDatePickerField = (
  page: ContentFlowPage,
  elementId: string,
  value: ContentFlowDatePicker,
): ContentFlowPage => {
  const elements = [...page.elements]
  const elementIndex = elements.findIndex((element) => element.questionId === elementId)

  if (elementIndex === -1) {
    return page
  }

  const targetElement = elements[elementIndex]

  if (!targetElement.datePicker) {
    return page
  }

  elements[elementIndex] = {
    ...targetElement,
    datePicker: value,
  }

  return {
    ...page,
    elements,
  }
}

export const updateElementFooter = (page: ContentFlowPage, elementId: string, value: string): ContentFlowPage => {
  const elements = [...page.elements]
  const elementIndex = elements.findIndex((element) => element.questionId === elementId)

  if (elementIndex === -1) {
    return page
  }

  const targetElement = elements[elementIndex]

  if (!targetElement.footer) {
    return page
  }

  if (value.length > FOOTER_LABEL_MAX_LENGTH) {
    return page
  }

  const updatedFooterElement = {
    ...targetElement,
    footer: {
      ...targetElement.footer,
      label: value,
    },
  }

  elements.splice(elementIndex, 1)
  elements.push(updatedFooterElement)

  return {
    ...page,
    elements,
  }
}

export const updateElementSelectField = (
  page: ContentFlowPage,
  elementId: string,
  key: 'singleSelect' | 'multiSelect',
  value: ContentFlowSingleSelect | ContentFlowMultiSelect,
): ContentFlowPage => {
  const elements = [...page.elements]
  const elementIndex = elements.findIndex((element) => element.questionId === elementId)

  if (elementIndex === -1) {
    return page
  }

  const targetElement = elements[elementIndex]

  const selectField = targetElement[key]

  if (!selectField) {
    return page
  }

  elements[elementIndex] = {
    ...targetElement,
    [key]: value,
  }

  return {
    ...page,
    elements,
  }
}
